import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useRefetch } from "~/hooks/useRefetch"
import { Heading, Text, Button, Toast, useToast } from "@oysterhr/ui"
import { TransactionsArea } from "~/app/pages/payments/TransactionsArea"

// flows
import { companyPaymentMethodsPath } from "@oysterhr/router"

import { useCompanyPaymentMethods } from "./hooks/useCompanyPaymentMethods"

// alerts
import { WarningAuthorizeAlert } from "./alerts/WarningAuthorizeAlert"
import { WarningVerifyAlert } from "./alerts/WarningVerifyAlert"
import { InfoCreditorsIdAlert } from "./alerts/InfoCreditorsIdAlert"
import { InfoShareMandateAlert } from "./alerts/InfoShareMandateAlert"
import { ErrorVerificationCountAlert } from "./alerts/ErrorVerificationCountAlert"
import { DeactivationAlert } from "./alerts/DeactivationAlert"

import { ReplacePaymentMethodModal } from "./modals/ReplacePaymentMethodModal"

import { WithoutConnectedPaymentMethod } from "./components/WithoutConnectedPaymentMethod/WithoutConnectedPaymentMethod"
import { DeletePaymentMethodModal } from "./modals/DeletePaymentMethodModal"
import { CompanyBankPaymentMethodCard } from "./components/CompanyPaymentMethodCard/CompanyPaymentMethodCard"
import { CompanyCurrencyEnumType, PaymentMethodState } from "@oysterhr/schema"
import { NewCompanyPaymentMethodsPage } from "./NewCompanyPaymentMethodsPage"
import { useCompanyPaymentsSettingsContext } from "./contexts/CompanyPaymentsSettingsContext"
import { useEffectOnce } from "@oysterhr/common"

function OldCompanyPaymentMethodsPage() {
  /* Hooks */
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/LandingPage")
  const toast = useToast()
  const navigate = useNavigate()
  const successToastDisplayedRef = useRef(false)
  const { accountAspect: companyId } = useAccountAspect()
  const { data, loading, refetch } = useCompanyPaymentMethods({ variables: { companyId } })
  useRefetch(loading, refetch)

  /* Local state */
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [authorizationInProgressInterval, setAuthorizationInProgressInterval] =
    useState<NodeJS.Timer | null>(null)

  const [showReplacePaymentMethodModal, setShowReplacePaymentMethodModal] = useState(false)

  const authorizationInProgress =
    location.pathname.split("/").slice(-1)[0] === "processing-athorization"

  /* Derived state */
  const paymentMethod = data?.company?.paymentMethod
  const paymentCurrencyCode = data?.company?.paymentCurrencyCode
  const transactions = data?.company?.transactions

  const onPaymentMethodDelectionSuccess = () => {
    refetch()
  }

  /* Effects */
  useEffectOnce(() => {
    const stripeCheckoutCompleted =
      location.pathname.split("/").slice(-1)[0] === "processing-checkout"

    if (!stripeCheckoutCompleted || successToastDisplayedRef.current) return

    successToastDisplayedRef.current = true
    toast({
      render: (
        <Toast variation="success">
          <Toast.Title>{t("toasts.connected.title")}</Toast.Title>
          <Toast.Content>{t("toasts.connected.description")}</Toast.Content>
        </Toast>
      ),
    })
    navigate(companyPaymentMethodsPath(companyId), { replace: true })
  })

  useEffect(() => {
    // Finish polling for authorization status
    if (
      paymentMethod?.state === PaymentMethodState.AuthorizedForDirectDebit &&
      authorizationInProgressInterval
    ) {
      clearInterval(authorizationInProgressInterval)
      setAuthorizationInProgressInterval(null)
      toast({
        render: (
          <Toast variation="success">
            <Toast.Title>{t("toasts.connected.title")}</Toast.Title>
            <Toast.Content>{t("toasts.connected.description")}</Toast.Content>
          </Toast>
        ),
      })

      navigate(companyPaymentMethodsPath(companyId))
    }

    // Start polling for authorization status
    if (authorizationInProgress && !authorizationInProgressInterval) {
      setAuthorizationInProgressInterval(
        setInterval(() => {
          refetch()
        }, 5000),
      )
    }

    return () => {
      if (authorizationInProgressInterval) {
        clearInterval(authorizationInProgressInterval)
      }
    }
  }, [
    paymentMethod,
    authorizationInProgress,
    authorizationInProgressInterval,
    companyId,
    navigate,
    refetch,
    toast,
    t,
  ])

  const showConnectPaymentMethod = !loading && !paymentMethod && paymentCurrencyCode

  return (
    <>
      <div className="p-8">
        <div className="flex items-center justify-between">
          <Heading as="h2" className="font-semibold">
            {t("title")}
          </Heading>
          {paymentMethod && (
            <Button
              data-analytics-name="company_payment_methods-replace-payment-method"
              onClick={() => setShowReplacePaymentMethodModal(true)}
            >
              {t("replacePaymentMethod")}
            </Button>
          )}
        </div>
        <Text className="mt-4 text-gray-400">{t("subtitle")}</Text>
        {paymentMethod && (
          <div className="mt-6 flex flex-col gap-2">
            {/* Warning Banners */}
            <WarningAuthorizeAlert paymentMethod={paymentMethod} />
            <WarningVerifyAlert paymentMethod={paymentMethod} />

            {/* Info Banners */}
            <InfoCreditorsIdAlert paymentMethod={paymentMethod} />
            <InfoShareMandateAlert paymentMethod={paymentMethod} />

            <ErrorVerificationCountAlert paymentMethod={paymentMethod} refetch={refetch} />
            <DeactivationAlert
              paymentMethod={paymentMethod}
              onDelete={() => setShowDeleteModal(true)}
            />
          </div>
        )}
        {showConnectPaymentMethod && (
          <WithoutConnectedPaymentMethod
            directDebitSupported={data.company.directDebitSupported}
            paymentCurrencyCode={paymentCurrencyCode as CompanyCurrencyEnumType}
          />
        )}
        <div className="mt-12 flex flex-col gap-x-0 gap-y-8 lg:flex-row lg:gap-x-8 lg:gap-y-0">
          <div className="w-full lg:w-1/2">
            {paymentMethod && (
              <CompanyBankPaymentMethodCard
                key={paymentMethod.__typename}
                paymentMethod={paymentMethod}
                stateLoading={!!(authorizationInProgress && authorizationInProgressInterval)}
                setShowDeleteModal={setShowDeleteModal}
              />
            )}
          </div>

          {paymentMethod && !!transactions?.length && (
            <div className="w-full lg:w-1/2">
              <TransactionsArea transactions={transactions} />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {paymentMethod && (
        <>
          <ReplacePaymentMethodModal
            isOpen={showReplacePaymentMethodModal}
            onClose={() => setShowReplacePaymentMethodModal(false)}
            paymentMethod={paymentMethod}
            onDeletionSuccess={onPaymentMethodDelectionSuccess}
          />
          <DeletePaymentMethodModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onDeletionSuccess={onPaymentMethodDelectionSuccess}
            paymentMethod={paymentMethod}
          />
        </>
      )}
    </>
  )
}

export function CompanyPaymentMethodsPage() {
  const { paymentsMandateSetup } = useCompanyPaymentsSettingsContext()

  return paymentsMandateSetup ? <NewCompanyPaymentMethodsPage /> : <OldCompanyPaymentMethodsPage />
}
